import { useState } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown,
  HelpCircle,
  Truck,
  CreditCard,
  RefreshCw,
  Phone,
  MapPin,
} from 'lucide-react';
import SeoHead from '@/components/SeoHead';

const faqCategories = [
  {
    id: 'products',
    title: 'Products & Sizing',
    icon: HelpCircle,
    questions: [
      {
        question: 'How do I find the right shoe size?',
        answer:
          "We recommend measuring your foot length and width. Use our size guide on each product page, which includes detailed measurements. If you're between sizes, we suggest going up half a size for comfort.",
      },
      {
        question: 'What materials are your shoes made from?',
        answer:
          'Our shoes are crafted from high-quality materials including genuine leather, synthetic leather, canvas, and breathable mesh. Each product description details the specific materials used.',
      },
      {
        question: 'How should I care for my shoes?',
        answer:
          'Clean with a soft brush and mild soap solution. For leather shoes, use a leather conditioner. Store in a cool, dry place away from direct sunlight. Avoid wearing in extreme weather conditions.',
      },
      {
        question: 'Do you offer custom sizing?',
        answer:
          'Currently, we offer standard sizing from EU 35-46. If you need custom sizes, please contact our customer service team for special arrangements.',
      },
    ],
  },
  {
    id: 'shipping',
    title: 'Shipping & Delivery',
    icon: Truck,
    questions: [
      {
        question: 'How long does delivery take?',
        answer:
          'Our packages take 24 hours to reach clients countrywide. This means fast delivery from Nairobi to every part of Kenya.',
      },
      {
        question: 'What are your shipping costs?',
        answer:
          'Free shipping on orders over KES 5,000 within Kenya. Standard shipping is KES 300 for orders under KES 5,000. Express delivery is KES 800.',
      },
      {
        question: 'Do you ship outside Kenya?',
        answer:
          "Currently, we only ship within Kenya. We're working on expanding our delivery network to cover East Africa in the coming months.",
      },
      {
        question: 'Can I track my order?',
        answer:
          "Yes! Once your order ships, you'll receive a tracking number via SMS and email. You can track your package in real-time through our website or courier partner's app.",
      },
    ],
  },
  {
    id: 'returns',
    title: 'Returns & Exchanges',
    icon: RefreshCw,
    questions: [
      {
        question: 'What is your return policy?',
        answer: `At Trendy Quest, we want you to be fully satisfied with your purchase. If for any reason you are not happy with your order, you may return or exchange it in accordance with the policy below.

1. Eligibility for Returns
- Items must be returned within 3 days of delivery.
- Products must be unused, unworn, and in their original packaging (including shoe box, tags, and accessories).
- Proof of purchase (receipt or order confirmation) is required.
- Certain items such as discounted or clearance items may not be eligible for return unless defective.

2. Exchanges
- If you would like a different size, color, or style, you may request an exchange, subject to availability.
- If the requested item is unavailable, we can issue a store credit or refund.

3. Refunds
- Refunds will be processed once we receive and inspect the returned product.
- Approved refunds will be credited to your original payment method within 2 business days.
- Shipping fees are non-refundable unless the return is due to our error, such as the wrong or defective item.

4. Defective or Incorrect Items
- If you receive a defective, damaged, or incorrect item, please contact us immediately.
- We will cover return shipping costs and send you a replacement or issue a full refund.

5. Return Shipping
- Customers are responsible for return shipping costs unless the item is defective or incorrect.
- We recommend using a trackable shipping service to ensure the item reaches us.

6. How to Initiate a Return or Exchange
- To request a return, refund, or exchange, please contact us.
- Email: info@trendyquestcollection.com
- Phone: +254717002340
- Address: Dubai Merchant Mall, Ronald Ngala Street, Shop G18, Nairobi

7. Non-Returnable Items
- Gift cards or store credits.
- Clearance or final-sale items unless faulty.
- Products not returned in original condition.

8. Policy Updates
- We may update or modify this Refund & Returns Policy from time to time. Changes will be posted on this page with the revised date.`,
      },
      {
        question: 'How do I initiate a return?',
        answer:
          "Contact our customer service team within 3 days of delivery. We'll provide a return authorization and return instructions so you can send the item back quickly.",
      },
      {
        question: 'Can I exchange for a different size?',
        answer:
          "Yes. Size exchanges are possible within 3 days for unworn shoes in original packaging. Simply contact us and we'll arrange for the correct size to be sent to you.",
      },
      {
        question: 'What if my shoes are damaged?',
        answer:
          "If you receive damaged shoes, please contact us immediately with photos. We'll arrange for a replacement or full refund at no cost to you.",
      },
    ],
  },
  {
    id: 'payment',
    title: 'Payment & Security',
    icon: CreditCard,
    questions: [
      {
        question: 'What payment methods do you accept?',
        answer:
          'We accept M-Pesa, card payments (Visa, Mastercard), bank transfers, and cash on delivery. All payments are processed securely through encrypted channels.',
      },
      {
        question: 'Is my payment information secure?',
        answer:
          'Absolutely. We use industry-standard SSL encryption and work with trusted payment processors. Your financial information is never stored on our servers.',
      },
      {
        question: 'Can I pay on delivery?',
        answer:
          'Yes, we offer pay on delivery through our partners Speedaf, Pickup Mtaani, and Parcel Grid. This service is available at checkout and may include a small delivery fee.',
      },
      {
        question: 'Do you offer installment payments?',
        answer:
          'We partner with select mobile money providers for installment options. Contact our sales team for orders over KES 10,000 to discuss payment plans.',
      },
    ],
  },
  {
    id: 'support',
    title: 'Customer Support',
    icon: Phone,
    questions: [
      {
        question: 'How can I contact customer service?',
        answer:
          'Reach us via WhatsApp at +254717002340, email at info@trendyquestcollection.com, or visit our store at Dubai Merchant Mall, Ronald Ngala Street, Shop G18.',
      },
      {
        question: 'What are your business hours?',
        answer:
          'Our physical store is open Monday-Saturday 9AM-7PM, Sunday 10AM-5PM. Online customer support is available 24/7 via WhatsApp and email.',
      },
      {
        question: 'Do you offer styling advice?',
        answer:
          'Yes. Our fashion experts are available to help you choose the perfect shoes for any occasion. Contact us for personalized recommendations.',
      },
      {
        question: 'How do I leave a review?',
        answer:
          "After receiving your order, you'll receive an email with a review link. You can also leave reviews directly on our website or social media pages.",
      },
    ],
  },
  {
    id: 'terms',
    title: 'Terms & Conditions',
    icon: MapPin,
    questions: [
      {
        question: 'Where can I find the full Terms & Conditions?',
        answer:
          'Our full Terms & Conditions are available on this page in the Full Terms & Conditions section below. You must read and accept them before using the site. Acceptance is recorded with a timestamp, browser details, and IP address for compliance.',
      },
      {
        question: 'What is covered by these Terms & Conditions?',
        answer:
          'These terms cover how to use the site, return and refund rules, delivery expectations, payment methods including pay-on-delivery with Speedaf, Pickup Mtaani, and Parcel Grid, and our rights to update the policy.',
      },
      {
        question: 'How do I accept the Terms & Conditions?',
        answer:
          'You will see a terms acceptance prompt when you first visit the site. You must accept the terms to continue shopping, and a link to the full Terms & Conditions is provided if you want to read them first.',
      },
    ],
  },
];

const fullTermsSections = [
  {
    title: '1. Acceptance of these terms',
    body: [
      'By accessing or using Trendy Quest Collection, you agree to these Terms & Conditions and any related policies referenced on the site.',
      'If you do not agree, you should not use the website or place an order.',
      'Acceptance of the terms is required before using the store and may be recorded with a timestamp, browser details, path, and IP address for compliance and fraud prevention.',
    ],
  },
  {
    title: '2. Products and availability',
    body: [
      'We sell footwear and clothing through our website, WhatsApp-assisted checkout, and our physical store.',
      'Product images, names, sizes, colors, descriptions, and prices are provided for guidance, but small variations may happen because of photography, screen settings, supplier changes, or stock updates.',
      'Availability is not guaranteed until your order has been confirmed. We may limit quantities per order or cancel orders affected by pricing, stock, or listing errors.',
    ],
  },
  {
    title: '3. Pricing and payments',
    body: [
      'All prices are shown in Kenya Shillings unless stated otherwise.',
      'We may update prices, discounts, delivery fees, and promotional offers at any time before order confirmation.',
      'Accepted payment methods may include M-Pesa, card payments, bank transfer, and pay on delivery where available.',
      'Where pay on delivery is offered through partners such as Speedaf, Pickup Mtaani, or Parcel Grid, extra courier, handling, or parcel commitment fees may apply and will be disclosed during checkout or order confirmation.',
    ],
  },
  {
    title: '4. Orders and confirmation',
    body: [
      'Submitting an order request does not automatically mean the order has been accepted.',
      'An order becomes confirmed only after we verify product availability, pricing, payment status, delivery details, and any courier requirements.',
      'We reserve the right to decline, pause, or cancel an order where we reasonably suspect fraud, pricing error, abuse, duplicate requests, unavailable stock, or incomplete customer details.',
    ],
  },
  {
    title: '5. Delivery, collection, and timing',
    body: [
      'Delivery timelines are estimates and depend on destination, courier coverage, payment confirmation, order volume, and customer responsiveness.',
      "Courier or pickup-point deliveries require accurate route, phone, and customer details. Delays caused by incorrect contact, location, or collection information are the customer's responsibility.",
      'Risk in the order passes to the customer once the item is delivered, collected, or accepted by the nominated recipient or pickup point.',
    ],
  },
  {
    title: '6. Returns, exchanges, and refunds',
    body: [
      'Returns and exchanges are subject to the Returns & Exchanges policy shown on this page.',
      'Unless the item is defective, damaged, or incorrect, products must be returned unused, unworn, and in original packaging within the stated return window.',
      'Refunds are issued only after inspection and approval. Original delivery charges are not refundable unless the error was ours.',
    ],
  },
  {
    title: '7. Customer responsibilities',
    body: [
      'You agree to provide accurate billing, delivery, and contact details when placing an order.',
      'You must not misuse the website, interfere with its operation, attempt unauthorized access, upload harmful material, or use the store for fraudulent or unlawful activity.',
      'If you place an order on behalf of another person, you confirm that you have authority to do so and that the delivery details you provide are accurate.',
    ],
  },
  {
    title: '8. Intellectual property',
    body: [
      'All content on the site, including branding, logos, product photography, layout, copy, graphics, and code, belongs to Trendy Quest Collection or its licensors unless stated otherwise.',
      'You may not copy, republish, distribute, modify, or commercially exploit site content without prior written permission.',
    ],
  },
  {
    title: '9. Liability and disclaimers',
    body: [
      'We work to keep the website accurate, secure, and available, but we do not guarantee uninterrupted access or that all content will always be error-free.',
      'To the maximum extent allowed by law, Trendy Quest Collection is not liable for indirect, incidental, consequential, or special losses arising from use of the website, delayed delivery, failed courier service, or third-party platform outages.',
      'Nothing in these terms limits liability where such limitation is not allowed by applicable law.',
    ],
  },
  {
    title: '10. Privacy and communications',
    body: [
      'Personal information submitted through the website, checkout, or WhatsApp-assisted ordering is handled under our privacy practices and used to process orders, provide support, prevent fraud, and improve service.',
      'By placing an order or contacting us, you agree that we may contact you by phone, SMS, email, or WhatsApp about your order, delivery, returns, support, or policy-related notices.',
    ],
  },
  {
    title: '11. Changes to the terms',
    body: [
      'We may revise these Terms & Conditions from time to time to reflect operational, legal, or policy updates.',
      'The version published on this page is the current version. Continued use of the site after an update means you accept the revised terms.',
    ],
  },
  {
    title: '12. Contact details',
    body: [
      'For questions about these terms, orders, refunds, or delivery support, contact Trendy Quest Collection via WhatsApp or phone at +254717002340, email at pattohkarimi@gmail.com, or visit Dubai Merchant Mall, Ronald Ngala Street, First Floor, Shop G18, Nairobi.',
    ],
  },
];

function FAQItem({ question, answer, isOpen, onToggle }) {
  return (
    <motion.div
      initial={false}
      className="last:border-b-0 border-b border-[#e8dcc3]"
    >
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between rounded-lg px-6 py-6 text-left transition-colors hover:bg-[#f9f2e4]"
      >
        <span className="pr-4 font-semibold text-[#111111]">{question}</span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className="h-5 w-5 flex-shrink-0 text-[#6a665f]" />
        </motion.div>
      </button>

      <AnimatePresence>
        {isOpen ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 leading-relaxed text-[#47433d]">
              {answer}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </motion.div>
  );
}

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqCategories.flatMap((category) =>
    category.questions.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer.replace(/\n+/g, ' ').trim(),
      },
    }))
  ),
};

export default function FAQPage() {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (categoryId, questionIndex) => {
    const key = `${categoryId}-${questionIndex}`;
    setOpenItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <>
      <SeoHead
        title="FAQ | Frequently Asked Questions | Trendy Quest Collection"
        description="Find answers to common questions about Trendy Quest Collection products, shipping, returns, payments, and customer service. Get help with sizing, delivery, and more."
        path="/faq"
        structuredData={faqSchema}
      />

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <section className="relative overflow-hidden bg-[#fffdf7] py-16 sm:py-20 lg:py-24">
          <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,250,235,0.8),rgba(255,247,214,0.4),rgba(18,18,18,0.1))]" />
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="mx-auto mb-6 inline-flex rounded-2xl bg-green px-5 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-white shadow-[0_10px_30px_rgba(20,68,37,0.28)]"
              >
                Help Center
              </motion.div>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.6 }}
                className="mb-6 text-4xl font-playfair font-bold text-[#111111] sm:text-5xl lg:text-6xl"
              >
                Frequently Asked Questions
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="mx-auto max-w-2xl text-lg text-[#47433d] sm:text-xl"
              >
                Everything you need to know about shopping with Trendy Quest
                Collection. Can&apos;t find what you&apos;re looking for? Contact our
                friendly team.
              </motion.p>
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 lg:py-24">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <div className="space-y-8">
              {faqCategories.map((category, categoryIndex) => {
                const IconComponent = category.icon;

                return (
                  <motion.div
                    id={category.id}
                    key={category.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: categoryIndex * 0.1, duration: 0.6 }}
                    className="overflow-hidden rounded-[2.2rem] border border-[#e8dcc3] bg-white shadow-[0_18px_50px_rgba(20,68,37,0.06)]"
                  >
                    <div className="border-b border-[#e8dcc3] bg-[#f6f3ee] px-6 py-4">
                      <div className="flex items-center gap-3">
                        <IconComponent className="h-6 w-6 text-green" />
                        <h2 className="text-xl font-playfair font-bold text-[#111111]">
                          {category.title}
                        </h2>
                      </div>
                    </div>

                    <div className="divide-y divide-[#f0e8d8]">
                      {category.questions.map((item, questionIndex) => (
                        <FAQItem
                          key={item.question}
                          question={item.question}
                          answer={item.answer}
                          isOpen={openItems[`${category.id}-${questionIndex}`]}
                          onToggle={() => toggleItem(category.id, questionIndex)}
                        />
                      ))}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <section id="full-terms" className="bg-[#fcfaf6] py-16 sm:py-20 lg:py-24">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="overflow-hidden rounded-[2.2rem] border border-[#e8dcc3] bg-white shadow-[0_18px_50px_rgba(20,68,37,0.06)]"
            >
              <div className="border-b border-[#e8dcc3] bg-[#f6f3ee] px-6 py-5">
                <p className="text-sm font-semibold uppercase tracking-[0.24em] text-green">
                  Full Legal Text
                </p>
                <h2 className="mt-2 text-2xl font-playfair font-bold text-[#111111] sm:text-3xl">
                  Full Terms &amp; Conditions
                </h2>
                <p className="mt-3 max-w-3xl text-sm leading-7 text-[#47433d] sm:text-base">
                  These terms govern access to Trendy Quest Collection, website
                  browsing, WhatsApp-assisted checkout, payments, delivery,
                  returns, and use of our product content and store services.
                </p>
              </div>

              <div className="space-y-8 px-4 py-5 sm:px-8 sm:py-8">
                {fullTermsSections.map((section) => (
                  <div key={section.title}>
                    <h3 className="text-lg font-semibold text-[#111111] sm:text-xl">
                      {section.title}
                    </h3>
                    <div className="mt-3 space-y-3 text-sm leading-7 text-[#47433d] sm:text-base">
                      {section.body.map((paragraph) => (
                        <p key={paragraph}>{paragraph}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        <section className="bg-[#f6f3ee] py-16 sm:py-20 lg:py-24">
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="rounded-[1.35rem] border border-[#ebe3ce] bg-white/80 p-4 shadow-[0_18px_50px_rgba(20,68,37,0.06)] backdrop-blur-sm sm:rounded-[2.2rem] sm:p-12"
            >
              <div className="mb-6 flex justify-center">
                <Phone className="h-12 w-12 text-green" />
              </div>
              <h2 className="mb-4 text-3xl font-playfair font-bold text-[#111111] sm:text-4xl">
                Still Need Help?
              </h2>
              <p className="mb-8 text-lg text-[#47433d]">
                Our customer service team is here to help. Reach out to us
                anytime.
              </p>
              <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
                <a
                  href="https://wa.me/254717002340"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex w-full items-center justify-center rounded-full bg-green px-5 py-3 text-base font-semibold text-white transition hover:bg-[#24552d] sm:w-auto sm:px-10 sm:py-4 sm:text-lg"
                >
                  WhatsApp Support
                </a>
                <Link
                  href="/contact"
                  className="inline-flex w-full items-center justify-center rounded-full border-2 border-green px-5 py-3 text-base font-semibold text-green transition hover:bg-green hover:text-white sm:w-auto sm:px-10 sm:py-4 sm:text-lg"
                >
                  Contact Form
                </Link>
              </div>
              <div className="mt-6 flex items-center justify-center gap-2 text-sm text-[#6a665f]">
                <MapPin className="h-4 w-4" />
                <span>
                  Dubai Merchant Mall, Ronald Ngala Street, Shop G18, Nairobi
                </span>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}
